<script>
  import { onMount } from 'svelte'
  import Button from '@/components/core/ui/buttons/v2.0.0/Button.svelte'

  import AlertBox from '@/components/core/extensions/alert_box/v1.0.0/AlertBox'

  export let platform
  export let addBlock

  const { store } = platform
  const { config } = platform.storeData

  const pluginRefs = []

  let savedBlocks = []
  let loaded = false

  let confirmDeleteOpen = false
  let deletingCb = {}

  let user

  async function getBucketId (bucketName) {
    const res = await platform.api.cms.buckets.find({
      where: `bucket.name = ?`,
      whereArgs: [bucketName],
    })

    return res.id
  }

  async function getSavedBlocks () {
    try {
      const bucketId = await getBucketId('customContentBlock')

      const res = await platform.api.cms.entities.findAllPages({
        where: `entity.bucketId = ?`,
        whereArgs: [bucketId],
      })

      return res.resources
    } catch (err) {
      return []
    }
  }

  function addContentBlock (block) {
    addBlock({
      layout: block.layout,
      plugins: block.plugins,
    })
  }

  function openDeleteCbModal (e, cb) {
    e.stopPropagation()

    deletingCb = cb
    confirmDeleteOpen = true
  }

  function closeDeleteModal () {
    deletingCb = {}
    confirmDeleteOpen = false
  }

  async function deleteContentBlock (id) {
    try {
      await platform.api.cms.entities.remove({
        where: `entity.id = ?`,
        whereArgs: [id],
      })
    } catch (error) {
      console.error(error)
    }

    savedBlocks = savedBlocks.filter(block => block.id !== id)
    closeDeleteModal()
  }

  onMount(async () => {
    user = await platform.api.cms.users.find($store.auth.claims.user)

    const res = await getSavedBlocks()
    if (res.length) {
      for (const block of res) {
        const savedBlock = JSON.parse(block.payload)
        savedBlock.name = block.name
        savedBlock.layout = structuredClone($store.layouts.find(l => l.name === savedBlock.contentBlock.layoutName))
        savedBlock.layout.fields = savedBlock.contentBlock.fields
        savedBlock.id = block.id

        if (savedBlock?.plugins?.length) {
          for (const plugin of savedBlock.plugins) {
            try {
              plugin.rowName = plugin?.nodeName?.replace(`${savedBlock.contentBlock.layoutName}-`, '')
              plugin.rowName = plugin?.rowName?.replace(`${savedBlock.contentBlock.nodeName}-`, '')
            } catch {
              console.error('Missing information! D:')

              continue
            }

            pluginRefs.push(plugin.ref)
          }
        } else {
          savedBlock.plugins = []
        }

        savedBlocks.push(savedBlock)
      }
    }

    await platform.api.core.loadComponents([...pluginRefs], { missing: config.components.missing })

    savedBlocks = savedBlocks
    loaded = true
  })

  function downloadBlock (block) {
    const filename = prompt('Enter the filename for the JSON file:', block.name)
    if (filename) {
      const json = JSON.stringify(block, null, 2)
      const blob = new Blob([json], { type: 'application/json' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `${block.name}.json`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
    }
  }

  async function getPluginFields (plugin) {
    let fields = JSON.parse(plugin.fields)

    if (plugin.pluginName) {
      try {
        const res = await platform.api.cms.plugins.find({
          where: `plugin.name = ?`,
          whereArgs: [plugin.pluginName],
        })

        if (res.fields) {
          fields = res.fields
        }
      } catch (err) {
        console.error('Possibly missing: ', err)
      }
    }

    return fields
  }

  function getBlockStyles (block) {
    let contentBlockFontColour = block?.layout?.fields?.fontColour || 'inherit'
    let contentBlockBackgroundColour = block?.layout?.fields?.backgroundColour
    let contentBlockBackgroundImage = block?.layout?.fields?.backgroundImage
    let contentBlockBackgroundRepeat = block?.layout?.fields?.backgroundRepeat

    if (block?.layout?.fields?.styleSyncID) {
      const payload = $store.render.styleSyncIDs[block?.layout?.fields?.styleSyncID]?.payload
      if (payload) {
        contentBlockFontColour = payload.fontColour || 'inherit'
        contentBlockBackgroundColour = payload.backgroundColour
        contentBlockBackgroundImage = payload.backgroundImage
        contentBlockBackgroundRepeat = payload.backgroundRepeat
      }
    }

    return [
      `color: ${contentBlockFontColour};`,
      contentBlockBackgroundColour ? `background: ${contentBlockBackgroundColour};` : '',
      contentBlockBackgroundImage ? `
          background-image: url(${contentBlockBackgroundImage}?size=xl);
          background-repeat: ${contentBlockBackgroundRepeat || 'no-repeat'};
          background-size: ${contentBlockBackgroundRepeat === 'repeat' ? 'contain' : 'cover'};
          background-position: center;
        ` : '',
    ].join('\n')
  }

  function getBlockClasses (block) {
    let classes = `outside layout ${block.layout.classNames.join(' ')} ${block.layout.fields.classNames?.map(cn => cn.class).join(' ')}`

    if (block?.layout?.fields?.styleSyncID) {
      const payload = $store.render.styleSyncIDs[block?.layout?.fields?.styleSyncID]?.payload
      if (payload) {
        classes = `outside layout ${payload.classNames?.map(cn => cn.class).join(' ')}`
      }
    }

    return classes
  }
</script>

<!-- svelte-ignore a11y-click-events-have-key-events -->
<!-- svelte-ignore a11y-no-static-element-interactions -->

<AlertBox
  icon="delete"
  open={confirmDeleteOpen}
  heading="Delete Content Block"
  text="Are you sure you want to delete '{deletingCb.name}'?"
  cancelButton="No, Keep Block"
  confirmButton="Yes, Delete Block"
  on:cancel={closeDeleteModal}
  on:confirm={() => { deleteContentBlock(deletingCb.id) }}
/>

<div class="layouts add-layouts">
  {#if loaded}
    {#if savedBlocks.length}
      {#each savedBlocks as block}
        <div class="layout-container" on:click={() => addContentBlock(block)} data-master-style={block.layout?.fields?.styleSyncID || 'Unset'}>
          <div class="layout-container__inner">
            <div style={getBlockStyles(block)} class={getBlockClasses(block)}>
              {#each block.layout.rows[0] as row}
                <div class={`${row.classNames.join(' ')} row`}>
                  {#each block.plugins as plugin}
                    {#if plugin.rowName === row.name}
                      {@const module = platform.components[plugin.ref]}
                      {#await getPluginFields(plugin) then fields}
                        <svelte:component
                          this={module.default}
                          fields={fields}
                        />
                      {/await}
                    {/if}
                  {/each}
                </div>
              {/each}
            </div>
          </div>
          <div class="block-name" on:click|stopPropagation>
            <span>{block.name}</span>
            <div>
              {#if user?.person?.email.endsWith('@barkweb.co.uk')}
                <Button rounded icon='download' on:click={() => downloadBlock(block)} />
              {/if}
              <Button rounded type='danger' icon='delete' on:click={(event) => { openDeleteCbModal(event, block) }} />
            </div>
          </div>
        </div>
      {/each}
    {:else}
      <div class="no-cb-message">
        <p>You currently don't have any content blocks saved.</p>
        <div class="message">
          <span>To save a content block: hover over it while in edit mode and use</span>
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" d="M17.59 3.59c-.38-.38-.89-.59-1.42-.59H5c-1.11 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V7.83c0-.53-.21-1.04-.59-1.41l-2.82-2.83zM12 19c-1.66 0-3-1.34-3-3s1.34-3 3-3 3 1.34 3 3-1.34 3-3 3zm1-10H7c-1.1 0-2-.9-2-2s.9-2 2-2h6c1.1 0 2 .9 2 2s-.9 2-2 2z"/>
          </svg>
        </div>
      </div>
    {/if}
  {/if}
</div>

<style lang="scss">.layouts {
  display: grid;
  grid-template-columns: 100%;
  column-gap: 1rem;
  row-gap: 2rem;
  overflow-y: auto;
}

.layout-container {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}
.layout-container__inner {
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  position: relative;
  overflow: hidden;
  border: 1px solid rgb(218, 218, 218);
  border-radius: 1rem;
  transition: 0.3s;
}
.layout-container__inner::after {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: transparent;
  z-index: 1;
}
.layout-container__inner:hover {
  transform: translateY(-0.5rem);
  cursor: pointer;
}

.layout {
  width: 100%;
  margin-top: 0px;
  margin-bottom: 0px;
  background-repeat: no-repeat;
  background-size: cover;
  background-position: center;
}

.row {
  max-height: 100%;
}

.block-name {
  width: 100%;
  text-align: center;
  font-weight: bold;
  font-size: 1.1rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.block-name > div {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 0.5rem;
}

.no-cb-message p {
  margin: 10px 0px;
}
.no-cb-message svg {
  height: 25px;
  width: 25px;
}
.no-cb-message .message {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}</style>
<script context="module">export const svelteCompilerVersion = {"major":4,"minor":2,"patch":18};</script>